import React, { useCallback, useMemo } from 'react';
import { HtmlSanitized } from 'components/display/HtmlSanitized';
import { GuaranteeDto } from 'api/pricing';
import { BorrowerGuaranteeCardItem } from './BorrowerGuaranteeCardItem';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'components/forms/NumberSelector';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { useBorrowerContractGuarantees } from '../hooks/useBorrowerContractGuarantees';
import { BorrowerContractFunnelV2Dto } from 'api/pricing';
import { useMainBorrowerPricingState } from '../../../contexts/main-borrower-pricing/MainBorrowerPricingContext';

interface BorrowerContractGuaranteesProps {
  contractFunnel: BorrowerContractFunnelV2Dto;
}

export const BorrowerContractGuarantees: React.FC<BorrowerContractGuaranteesProps> = ({ contractFunnel }) => {
  const { t } = useTranslation(NsKey.Pricing);
  const { openDialog } = useDialog();
  const { guaranteesReferential } = useMainBorrowerPricingState()

  const {
    isActive,
    toggleGuarantee
  } = useBorrowerContractGuarantees({ contractFunnel });

  const requiredGuarantees = useMemo(() => {
    return guaranteesReferential.filter(guarantee => guarantee.isRequired)
  }, [guaranteesReferential])

  const optionalGuarantees = useMemo(() => {
    return guaranteesReferential.filter(guarantee => !guarantee.isRequired)
  }, [guaranteesReferential])

  const showGuaranteeDetails = useCallback(
    (title: string, longDescription: string) => {
      openDialog({
        title,
        size: Sizes["4xl"],
      },
        <HtmlSanitized html={longDescription} className="font-12 text-dark-light w-100" />
      );
    }, [openDialog]
  );

  const renderGuaranteeCard = useCallback((guarantee: GuaranteeDto) => {
    return (
      <BorrowerGuaranteeCardItem
        key={guarantee.id}
        isActive={isActive(guarantee.id)}
        guarantee={guarantee}
        isRequired={guarantee.isRequired}
        toggleGuarantee={() => !guarantee.isRequired && toggleGuarantee(guarantee)}
        showGuaranteeDetails={showGuaranteeDetails}
      />
    );
  }, [isActive, toggleGuarantee, showGuaranteeDetails]);

  return (
    <div className='flex flex-col gap-4'>
      <CardsSection title={t('PRICING.GUARANTEES.MANDATORY')}>
        {requiredGuarantees.map(renderGuaranteeCard)}
      </CardsSection>

      {optionalGuarantees.length > 0 && (
        <CardsSection title={t('PRICING.GUARANTEES.OPTIONAL')}>
          {optionalGuarantees.map(renderGuaranteeCard)}
        </CardsSection>
      )}
    </div>
  );
};
